import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, CheckCircle2, FileText, ExternalLink } from "lucide-react";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";

export default function SeoAdmin() {
  const [data, setData] = useState(null);
  const [pages, setPages] = useState([]);
  const [perm, setPerm] = useState([]);
  const [regen, setRegen] = useState(false);
  const [gaId, setGaId] = useState("");
  const [savingGa, setSavingGa] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/seo/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/location-pages").then((r) => setPages(r.data || [])).catch(() => {});
    api.get("/seo-pages").then((r) => setPerm(r.data || [])).catch(() => {});
    api.get("/admin/settings").then((r) => setGaId(r.data?.google_analytics_id || "")).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveGa = async () => {
    setSavingGa(true);
    try { await api.put("/admin/settings", { google_analytics_id: gaId.trim() }); toast.success("Google Analytics ID saved"); }
    catch { toast.error("Save failed"); } finally { setSavingGa(false); }
  };

  const regenerate = async () => {
    setRegen(true);
    try { await api.post("/admin/seo/regenerate-sitemap"); toast.success("Sitemap regenerated"); load(); }
    catch { toast.error("Failed"); } finally { setRegen(false); }
  };

  const savePage = async (p) => { try { await api.put(`/admin/location-pages/${p.id}`, p); toast.success(`${p.city} saved`); load(); } catch { toast.error("Save failed"); } };
  const setPageField = (i, k, v) => setPages((arr) => arr.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const setSection = (pi, si, k, v) => setPages((arr) => arr.map((x, j) => j === pi ? { ...x, sections: x.sections.map((s, sj) => sj === si ? { ...s, [k]: v } : s) } : x));

  if (!data) return <div className="text-slate-500">Loading...</div>;
  const metrics = [
    ["Total Pages", data.page_count], ["Inventory Pages", data.inventory_page_count],
    ["Location Pages", data.location_page_count], ["Permanent Pages", data.permanent_page_count],
  ];
  const warns = [
    ["Missing Titles", data.missing_title_count], ["Missing Meta", data.missing_meta_count],
    ["Missing Alt Text", data.missing_alt_count], ["Incomplete Listings", data.incomplete_count],
  ];

  return (
    <div data-testid="admin-seo">
      <AdminHeader title="SEO" subtitle="Sitemap, metadata health, and location landing pages"
        action={<Button onClick={regenerate} disabled={regen} className="bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full" data-testid="regenerate-sitemap-button"><RefreshCw size={16} className={regen ? "animate-spin" : ""} /> Regenerate Sitemap</Button>} />

      <Tabs defaultValue="dashboard">
        <TabsList className="mb-4"><TabsTrigger value="dashboard">Dashboard</TabsTrigger><TabsTrigger value="locations">Location Pages</TabsTrigger><TabsTrigger value="permanent">Permanent Pages</TabsTrigger></TabsList>

        <TabsContent value="dashboard">
          <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4" data-testid="analytics-settings">
            <h3 className="font-heading font-bold text-slate-900 mb-1">Analytics & Tracking</h3>
            <p className="text-xs text-slate-500 mb-3">Paste your Google Analytics 4 Measurement ID (it looks like G-XXXXXXXXXX). Once saved, the public site loads Google Analytics and reports page views automatically. Leave blank to turn it off.</p>
            <div className="flex gap-2 max-w-md">
              <Input value={gaId} onChange={(e) => setGaId(e.target.value)} placeholder="G-XXXXXXXXXX" className="font-mono" data-testid="ga-measurement-id" />
              <Button onClick={saveGa} disabled={savingGa} className="bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full whitespace-nowrap" data-testid="save-ga-button">{savingGa ? "Saving..." : "Save"}</Button>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 flex flex-wrap items-center gap-6">
            <div><p className="text-xs text-slate-400 font-mono uppercase">Sitemap Status</p><p className="font-semibold text-slate-900 flex items-center gap-1"><CheckCircle2 size={16} className="text-mtm-teal" /> {data.sitemap_status}</p></div>
            <div><p className="text-xs text-slate-400 font-mono uppercase">Last Updated</p><p className="font-semibold text-slate-900">{data.last_sitemap_update ? new Date(data.last_sitemap_update).toLocaleString() : "Never"}</p></div>
            <a href={`${api.defaults.baseURL}/sitemap.xml`} target="_blank" rel="noreferrer" className="text-sm text-mtm-purple font-semibold flex items-center gap-1 hover:underline">View sitemap.xml <ExternalLink size={13} /></a>
            <a href={`${api.defaults.baseURL}/robots.txt`} target="_blank" rel="noreferrer" className="text-sm text-mtm-purple font-semibold flex items-center gap-1 hover:underline">robots.txt <ExternalLink size={13} /></a>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {metrics.map(([l, v]) => (<div key={l} className="bg-white rounded-xl border border-slate-200 p-5"><div className="text-3xl font-heading font-extrabold text-slate-900">{v}</div><div className="text-xs text-slate-500 uppercase font-mono mt-1">{l}</div></div>))}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {warns.map(([l, v]) => (<div key={l} className={`rounded-xl border p-5 ${v ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}><div className={`text-3xl font-heading font-extrabold ${v ? "text-amber-600" : "text-slate-900"}`}>{v}</div><div className="text-xs text-slate-500 uppercase font-mono mt-1 flex items-center gap-1">{v > 0 && <AlertTriangle size={12} className="text-amber-500" />}{l}</div></div>))}
          </div>
          {(data.warnings?.incomplete || []).length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mt-4">
              <h3 className="font-heading font-bold text-slate-900 mb-3">Incomplete Listings</h3>
              <div className="space-y-2">{data.warnings.incomplete.map((r) => (<Link key={r.id} to={`/admin/inventory/${r.id}`} className="flex items-center gap-2 text-sm text-mtm-purple hover:underline"><FileText size={14} /> {r.label}</Link>))}</div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="locations">
          <div className="space-y-4">
            {pages.map((p, i) => (
              <details key={p.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`loc-page-${p.slug}`}>
                <summary className="font-heading font-bold text-slate-900 cursor-pointer flex items-center justify-between">{p.city} <span className="text-xs font-normal text-slate-400">/locations/{p.slug}</span></summary>
                <div className="mt-4 space-y-3">
                  <div><Label className="text-xs">Meta Title</Label><Input value={p.meta_title ?? ""} onChange={(e) => setPageField(i, "meta_title", e.target.value)} /></div>
                  <div><Label className="text-xs">Meta Description</Label><Textarea rows={2} value={p.meta_description ?? ""} onChange={(e) => setPageField(i, "meta_description", e.target.value)} /></div>
                  <div><Label className="text-xs">H1</Label><Input value={p.h1 ?? ""} onChange={(e) => setPageField(i, "h1", e.target.value)} /></div>
                  <div><Label className="text-xs">Intro</Label><Textarea rows={3} value={p.intro ?? ""} onChange={(e) => setPageField(i, "intro", e.target.value)} /></div>
                  {(p.sections || []).map((sec, si) => (
                    <div key={si} className="border-l-2 border-slate-100 pl-3 space-y-2">
                      <Input value={sec.heading ?? ""} onChange={(e) => setSection(i, si, "heading", e.target.value)} placeholder="Section heading" />
                      <Textarea rows={2} value={sec.body ?? ""} onChange={(e) => setSection(i, si, "body", e.target.value)} placeholder="Section body" />
                    </div>
                  ))}
                  <div><Label className="text-xs">CTA Copy</Label><Textarea rows={2} value={p.cta ?? ""} onChange={(e) => setPageField(i, "cta", e.target.value)} /></div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm"><Switch checked={!!p.active} onCheckedChange={(v) => setPageField(i, "active", v)} /> Active</label>
                    <Button onClick={() => savePage(pages[i])} className="bg-mtm-purple text-white rounded-full" data-testid={`save-loc-${p.slug}`}>Save {p.city}</Button>
                    <a href={`/locations/${p.slug}`} target="_blank" rel="noreferrer" className="text-sm text-mtm-purple hover:underline flex items-center gap-1">Preview <ExternalLink size={13} /></a>
                  </div>
                </div>
              </details>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="permanent">
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {perm.map((p) => (
              <a key={p.slug} href={`/guides/${p.slug}`} target="_blank" rel="noreferrer" className="flex items-center justify-between px-5 py-4 hover:bg-slate-50">
                <div><div className="font-semibold text-slate-900">{p.title}</div><div className="text-xs text-slate-400 font-mono">/guides/{p.slug}</div></div>
                <ExternalLink size={15} className="text-slate-400" />
              </a>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
